/**
 * Заказы клиентов / резервы под проект (SO) — Phase 2.5.
 *
 * В оригинальной экселе это «Отложено_расход». Workflow:
 *   менеджер создаёт SO «для проекта X нужно: 5 досок, 200 кирпича, отгрузить
 *   к дате Y» → кладовщик собирает → отгружает (создаётся outflow + декремент
 *   stocks). Открытые SO формируют reserved[item] в Планировании.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { ItemAutocomplete } from "@/components/warehouse/ItemAutocomplete";
import { usePermissions } from "@/lib/usePermissions";
import {
  fetchSalesOrders, fetchSalesOrder, createSalesOrder,
  updateSOStatus, cancelSalesOrder, shipSalesOrder,
  fetchObjects, formatObjectLabel,
  formatRub, SO_STATUS_LABEL,
  type SOItemRow, type SOStatus, type ItemRow, type ObjectForSelect,
} from "@/api/warehouse";
import { ApiError } from "@/api/client";


const STATUS_FILTERS: { value: SOStatus | ""; label: string }[] = [
  { value: "",            label: "Все" },
  { value: "draft",       label: SO_STATUS_LABEL.draft },
  { value: "confirmed",   label: SO_STATUS_LABEL.confirmed },
  { value: "partial",     label: SO_STATUS_LABEL.partial },
  { value: "shipped",     label: SO_STATUS_LABEL.shipped },
  { value: "cancelled",   label: SO_STATUS_LABEL.cancelled },
];

const STATUS_COLOR: Record<SOStatus, string> = {
  draft:     "var(--mute)",
  confirmed: "#3390ec",
  partial:   "#e99a12",
  shipped:   "#3D7A3D",
  cancelled: "var(--brick)",
};


export function WarehouseSalesOrdersPage() {
  const can = usePermissions();
  const [statusFilter, setStatusFilter] = useState<SOStatus | "">("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creatingOpen, setCreatingOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ["warehouse-so-list", statusFilter],
    queryFn: () => fetchSalesOrders({ status: statusFilter || undefined, limit: 200 }),
  });

  if (listQ.isLoading) return <Spinner size="lg" label="Загрузка заказов клиентов…" />;
  if (listQ.error) {
    const err = listQ.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }
  const list = listQ.data?.items || [];

  return (
    <div className="page">

      <div className="filter-bar" style={{ justifyContent: "space-between", marginBottom: "var(--gap)", flexWrap: "wrap", gap: 8 }}>
        <div className="chip-row">
          {STATUS_FILTERS.map(f => (
            <button key={f.value || "all"} type="button"
              className={`chip ${statusFilter === f.value ? "active" : ""}`}
              onClick={() => setStatusFilter(f.value)}>
              {f.label}
            </button>
          ))}
        </div>
        {can("so.create") && (
          <button type="button" className="btn btn-primary"
            onClick={() => setCreatingOpen(true)}
            style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            + Новый заказ
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div className="muted">Заказов клиентов пока нет</div>
          <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 8 }}>
            Создайте заказ под проект (с привязкой объекта) — открытые позиции автоматически попадут в «Резерв» на Планировании.
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: selectedId != null ? "1fr 1fr" : "1fr", gap: "var(--gap)" }}>
          <div className="card" style={{ padding: 0 }}>
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>№</th>
                    <th>Клиент / Объект</th>
                    <th style={{ width: 120 }}>Статус</th>
                    <th style={{ width: 110 }}>Заказан</th>
                    <th style={{ width: 110 }}>Отгрузить до</th>
                    <th className="num" style={{ width: 110 }}>Сумма</th>
                    <th className="num" style={{ width: 110 }}>Поз. / резерв</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(so => (
                    <tr key={so.id}
                        onClick={() => setSelectedId(so.id)}
                        style={{ cursor: "pointer", background: so.id === selectedId ? "var(--paper)" : undefined }}>
                      <td className="tabular">{so.doc_number || so.id}</td>
                      <td>
                        <div>{so.customer_name || <span className="muted">—</span>}</div>
                        {so.object_id && (
                          <div style={{ fontSize: 11, color: "var(--mute)" }}>
                            {so.object_client_name || `проект #${so.object_id}`}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ display: "inline-block", padding: "1px 8px", fontSize: 11,
                                        borderRadius: 10, background: "var(--paper)",
                                        color: STATUS_COLOR[so.status], fontWeight: 600 }}>
                          {SO_STATUS_LABEL[so.status]}
                        </span>
                      </td>
                      <td className="tabular" style={{ fontSize: 12 }}>{so.order_date}</td>
                      <td className="tabular" style={{ fontSize: 12 }}>{so.ship_by || <span className="muted">—</span>}</td>
                      <td className="num tabular">{formatRub(so.total_amount)}</td>
                      <td className="num tabular" style={{ fontSize: 12 }}>
                        {so.items_count}{so.pending_qty > 0 && <span style={{ color: "var(--brick)" }}> · ↻{so.pending_qty}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selectedId != null && (
            <SODetails id={selectedId} onClose={() => setSelectedId(null)} />
          )}
        </div>
      )}

      {creatingOpen && (
        <SOCreateModal onClose={() => setCreatingOpen(false)}
                       onCreated={(id) => { setSelectedId(id); setCreatingOpen(false); }} />
      )}
    </div>
  );
}


function SODetails({ id, onClose }: { id: number; onClose: () => void }) {
  const can = usePermissions();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["warehouse-so", id],
    queryFn: () => fetchSalesOrder(id),
  });

  const [shipOpen, setShipOpen] = useState(false);

  const setStatusMut = useMutation({
    mutationFn: (status: SOStatus) => updateSOStatus(id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-so", id] });
      void qc.invalidateQueries({ queryKey: ["warehouse-so-list"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-planning"] });
    },
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelSalesOrder(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-so-list"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-planning"] });
      onClose();
    },
  });

  if (q.isLoading) return <div className="card" style={{ padding: 16 }}><Spinner /></div>;
  if (!q.data) return null;
  const { doc, items } = q.data;
  const canEditStatus = !["shipped", "cancelled"].includes(doc.status);
  const canShip = ["confirmed", "partial"].includes(doc.status);

  return (
    <div className="card" style={{ padding: 16, position: "relative" }}>
      <button type="button" onClick={onClose}
        style={{ position: "absolute", top: 8, right: 8, background: "transparent", border: "none", fontSize: 20, color: "var(--mute)", cursor: "pointer" }}>×</button>
      <div style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700 }}>
        Заказ № {doc.doc_number || `#${doc.id}`}
      </div>
      <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4, marginBottom: 12 }}>
        {doc.customer_name && <>Клиент: <strong>{doc.customer_name}</strong> · </>}
        {doc.object_id && <>Объект: <strong>{doc.object_client_name || `#${doc.object_id}`}</strong> · </>}
        Заказан: {doc.order_date}{doc.ship_by && <> · Отгрузить до: {doc.ship_by}</>}
        {doc.warehouse && <> · Склад: {doc.warehouse}</>}
        {doc.address && <div style={{ marginTop: 4 }}>{doc.address}</div>}
        {doc.phone && <span>{doc.phone} </span>}
        {doc.payment_method && <span>{doc.payment_method}</span>}
        {doc.note && <div style={{ marginTop: 4 }}>{doc.note}</div>}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: "var(--mute)" }}>Статус:</span>
        {canEditStatus ? (
          <select value={doc.status} onChange={(e) => setStatusMut.mutate(e.target.value as SOStatus)} className="input-sm">
            <option value="draft">{SO_STATUS_LABEL.draft}</option>
            <option value="confirmed">{SO_STATUS_LABEL.confirmed}</option>
            <option value="partial">{SO_STATUS_LABEL.partial}</option>
            <option value="shipped">{SO_STATUS_LABEL.shipped}</option>
          </select>
        ) : (
          <span style={{ fontWeight: 600, color: STATUS_COLOR[doc.status] }}>{SO_STATUS_LABEL[doc.status]}</span>
        )}
        <div style={{ flex: 1 }}/>
        {canShip && can("so.ship") && (
          <button type="button" className="btn btn-sm btn-primary"
                  onClick={() => setShipOpen(true)}>
            <Icon name="arrow_up" size={12}/> Отгрузить
          </button>
        )}
        {!["cancelled", "shipped"].includes(doc.status) && (can("so.create") || can("so.edit")) && (
          <button type="button" className="btn btn-sm"
                  onClick={() => { if (confirm(`Отменить заказ № ${doc.doc_number || doc.id}?`)) cancelMut.mutate(); }}
                  style={{ color: "var(--brick)" }}>
            Отменить
          </button>
        )}
      </div>

      <div className="tbl-scroll">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Артикул</th>
              <th>Наименование</th>
              <th style={{ width: 70 }}>Ед.</th>
              <th className="num" style={{ width: 90 }}>Заказано</th>
              <th className="num" style={{ width: 90 }}>Отгружено</th>
              <th className="num" style={{ width: 90 }}>Резерв</th>
              <th className="num" style={{ width: 100 }}>Цена</th>
              <th className="num" style={{ width: 110 }}>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => {
              const left = it.quantity_ordered - it.quantity_shipped;
              return (
                <tr key={it.id}>
                  <td className="tabular">{it.sku || "—"}</td>
                  <td>{it.name}</td>
                  <td>{it.unit || "—"}</td>
                  <td className="num tabular">{it.quantity_ordered}</td>
                  <td className="num tabular">{it.quantity_shipped}</td>
                  <td className="num tabular strong" style={{ color: left > 0 ? "#e99a12" : "var(--mute)" }}>
                    {left > 0 ? left : "—"}
                  </td>
                  <td className="num tabular">{it.price != null ? formatRub(it.price) : "—"}</td>
                  <td className="num tabular">{it.amount != null ? formatRub(it.amount) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7} style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700 }}>Итого:</td>
              <td className="num tabular strong">{formatRub(doc.total_amount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {shipOpen && (
        <SOShipModal
          soId={doc.id}
          items={items}
          onClose={() => setShipOpen(false)}
          onShipped={() => {
            void qc.invalidateQueries({ queryKey: ["warehouse-so", id] });
            void qc.invalidateQueries({ queryKey: ["warehouse-so-list"] });
            void qc.invalidateQueries({ queryKey: ["warehouse-planning"] });
            void qc.invalidateQueries({ queryKey: ["warehouse-stocks"] });
            void qc.invalidateQueries({ queryKey: ["warehouse-overview"] });
            void qc.invalidateQueries({ queryKey: ["warehouse-outflow-list"] });
            setShipOpen(false);
          }}
        />
      )}
    </div>
  );
}


function SOShipModal({ soId, items, onClose, onShipped }: {
  soId: number;
  items: SOItemRow[];
  onClose: () => void;
  onShipped: () => void;
}) {
  const [qty, setQty] = useState<Record<number, string>>(
    Object.fromEntries(items.map(it => [it.item_id, String(it.quantity_ordered - it.quantity_shipped)]))
  );
  const [createOutflow, setCreateOutflow] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      const lines = items
        .map(it => {
          const v = parseFloat((qty[it.item_id] || "").replace(",", "."));
          if (isNaN(v) || v <= 0) return null;
          return { item_id: it.item_id, quantity_shipped: v, price: it.price };
        })
        .filter((x): x is { item_id: number; quantity_shipped: number; price: number | null } => x !== null);
      if (lines.length === 0) throw new Error("Введите хотя бы одно положительное количество");
      return shipSalesOrder(soId, lines, createOutflow);
    },
    onSuccess: onShipped,
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div role="dialog" onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.4)", display: "grid", placeItems: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="card"
           style={{ width: "100%", maxWidth: 700, padding: 20, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700 }}>
            Отгрузка по заказу № {soId}
          </div>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--mute)" }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 12 }}>
          Введите фактически отгруженное количество. По умолчанию — «осталось».
          При частичной отгрузке статус станет «Частично», остаток продолжит висеть в резерве.
        </div>
        <div className="tbl-scroll" style={{ marginBottom: 12 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Позиция</th>
                <th className="num">Заказано</th>
                <th className="num">Уже отгружено</th>
                <th className="num" style={{ width: 130 }}>Отгрузить сейчас</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const left = it.quantity_ordered - it.quantity_shipped;
                return (
                  <tr key={it.id} style={left <= 0 ? { opacity: 0.5 } : undefined}>
                    <td>{it.sku && <span className="tabular" style={{ color: "var(--mute)", marginRight: 6 }}>{it.sku}</span>}{it.name}</td>
                    <td className="num tabular">{it.quantity_ordered} {it.unit || ""}</td>
                    <td className="num tabular">{it.quantity_shipped}</td>
                    <td className="num">
                      <input type="text" inputMode="decimal" className="input-sm"
                             value={qty[it.item_id] ?? ""}
                             onChange={e => setQty(d => ({ ...d, [it.item_id]: e.target.value }))}
                             disabled={left <= 0}
                             style={{ width: "100%", textAlign: "right" }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 12 }}>
          <input type="checkbox" checked={createOutflow} onChange={e => setCreateOutflow(e.target.checked)} />
          Создать расходную накладную (с привязкой к объекту, если есть)
        </label>
        {error && <div className="alert alert-red" style={{ marginBottom: 8 }}><div className="alert-body">{error}</div></div>}
        <div style={{ textAlign: "right" }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginRight: 8 }}>Отмена</button>
          <button type="button" className="btn btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending}
                  style={{ background: "#3D7A3D", color: "#fff", border: "none", padding: "6px 18px", fontWeight: 600, cursor: "pointer" }}>
            {mut.isPending ? "Отгрузка…" : "Отгрузить"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SOCreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    customer_name: "",
    object_id: null as number | null,
    deal_amo_id: null as number | null,
    order_date: today,
    ship_by: "",
    address: "",
    phone: "",
    payment_method: "Продажа",
    warehouse: "СКЛАД 2_4",
    note: "",
  });
  const [lines, setLines] = useState<{ item: ItemRow; quantity: number; price: number }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const objectsQ = useQuery({
    queryKey: ["warehouse-objects"],
    queryFn: () => fetchObjects(),
    staleTime: 60_000,
  });
  const objects: ObjectForSelect[] = objectsQ.data || [];

  const total = useMemo(
    () => lines.reduce((s, l) => s + l.quantity * (l.price || 0), 0),
    [lines]
  );

  const mut = useMutation({
    mutationFn: () => createSalesOrder({
      customer_name: form.customer_name.trim() || null,
      object_id: form.object_id,
      deal_amo_id: form.deal_amo_id,
      order_date: form.order_date,
      ship_by: form.ship_by || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      payment_method: form.payment_method || null,
      warehouse: form.warehouse,
      note: form.note.trim() || null,
      items: lines.map(l => ({ item_id: l.item.id, quantity: l.quantity, price: l.price || null })),
    }),
    onSuccess: (data) => onCreated(data.id),
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  function addItem(it: ItemRow) {
    setLines(ls => {
      const i = ls.findIndex(l => l.item.id === it.id);
      if (i !== -1) {
        const updated = [...ls];
        updated[i] = { ...updated[i], quantity: updated[i].quantity + 1 };
        return updated;
      }
      return [...ls, { item: it, quantity: 1, price: it.price_sell ?? 0 }];
    });
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!lines.length) { setError("Добавьте хотя бы одну позицию"); return; }
    mut.mutate();
  }

  return (
    <div role="dialog" onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.4)", display: "grid", placeItems: "center", padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="card"
            style={{ width: "100%", maxWidth: 820, padding: 20, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700 }}>Новый заказ клиента</div>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--mute)" }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div>
            <Label>Клиент (ФИО / название)</Label>
            <input type="text" className="input-sm" style={{ width: "100%" }}
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="ФИО или название организации" />
          </div>
          <div>
            <Label>Объект (проект)</Label>
            <select className="input-sm" style={{ width: "100%" }}
              value={form.object_id ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") { setForm({ ...form, object_id: null, deal_amo_id: null }); return; }
                const id = parseInt(v, 10);
                const o = objects.find(x => x.id === id);
                setForm({
                  ...form,
                  object_id: id,
                  deal_amo_id: o?.deal_amo_id ?? null,
                  address: form.address || o?.address || "",
                  customer_name: form.customer_name || o?.client_name || "",
                });
              }}>
              <option value="">— Без привязки —</option>
              {objects.map(o => (
                <option key={o.id} value={o.id}>#{o.id} · {formatObjectLabel(o)}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Дата заказа</Label>
            <input type="date" className="input-sm" style={{ width: "100%" }} value={form.order_date}
                   onChange={e => setForm({ ...form, order_date: e.target.value })} />
          </div>
          <div>
            <Label>Отгрузить до</Label>
            <input type="date" className="input-sm" style={{ width: "100%" }} value={form.ship_by}
                   onChange={e => setForm({ ...form, ship_by: e.target.value })} />
          </div>
          <div>
            <Label>Склад</Label>
            <select className="input-sm" style={{ width: "100%" }} value={form.warehouse}
                    onChange={e => setForm({ ...form, warehouse: e.target.value })}>
              <option value="СКЛАД 2_4">СКЛАД 2_4</option>
              <option value="ИНСТРУМЕНТ 1.0">ИНСТРУМЕНТ 1.0</option>
              <option value="ОФИС">ОФИС</option>
            </select>
          </div>
          <div>
            <Label>Вид заявки</Label>
            <select className="input-sm" style={{ width: "100%" }} value={form.payment_method}
                    onChange={e => setForm({ ...form, payment_method: e.target.value })}>
              <option value="Продажа">Продажа</option>
              <option value="Доставка">Доставка</option>
              <option value="Самовывоз">Самовывоз</option>
              <option value="Внутреннее">Внутреннее</option>
            </select>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div>
            <Label>Адрес</Label>
            <input type="text" className="input-sm" style={{ width: "100%" }} value={form.address}
                   onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <Label>Телефон</Label>
            <input type="text" className="input-sm" style={{ width: "100%" }} value={form.phone}
                   onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>

        <Label>Примечание</Label>
        <textarea className="input-sm" style={{ width: "100%", minHeight: 50, marginBottom: 12 }}
                  value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />

        <div style={{ marginBottom: 8, padding: 8, background: "var(--paper)", borderRadius: 4, display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="search" size={14} />
          <span style={{ fontSize: 12, color: "var(--mute)" }}>Добавить позицию:</span>
          <div style={{ flex: 1, maxWidth: 500 }}>
            <ItemAutocomplete onSelect={addItem} placeholder="Поиск по артикулу или наименованию" />
          </div>
        </div>

        <div className="tbl-scroll" style={{ marginBottom: 12 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Позиция</th>
                <th className="num" style={{ width: 100 }}>Кол-во</th>
                <th className="num" style={{ width: 100 }}>Цена</th>
                <th className="num" style={{ width: 110 }}>Сумма</th>
                <th style={{ width: 36 }}/>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: 30, color: "var(--mute)" }}>
                  Добавьте первую позицию через поиск выше
                </td></tr>
              ) : lines.map((l, idx) => (
                <tr key={l.item.id}>
                  <td>
                    {l.item.sku && <span className="tabular" style={{ color: "var(--mute)", marginRight: 6 }}>{l.item.sku}</span>}
                    {l.item.name}
                  </td>
                  <td className="num">
                    <input type="text" inputMode="decimal" className="input-sm" style={{ width: "100%", textAlign: "right" }}
                           value={l.quantity}
                           onChange={e => {
                             const v = parseFloat(e.target.value.replace(",", ".")) || 0;
                             setLines(ls => ls.map((x, i) => i === idx ? { ...x, quantity: v } : x));
                           }} />
                  </td>
                  <td className="num">
                    <input type="text" inputMode="decimal" className="input-sm" style={{ width: "100%", textAlign: "right" }}
                           value={l.price}
                           onChange={e => {
                             const v = parseFloat(e.target.value.replace(",", ".")) || 0;
                             setLines(ls => ls.map((x, i) => i === idx ? { ...x, price: v } : x));
                           }} />
                  </td>
                  <td className="num tabular strong">{formatRub(l.quantity * l.price)}</td>
                  <td><button type="button" className="btn btn-sm" onClick={() => setLines(ls => ls.filter((_, i) => i !== idx))} style={{ color: "var(--brick)" }}>×</button></td>
                </tr>
              ))}
            </tbody>
            {lines.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700 }}>Итого:</td>
                  <td className="num tabular strong">{formatRub(total)}</td>
                  <td/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {error && <div className="alert alert-red" style={{ marginBottom: 8 }}><div className="alert-body">{error}</div></div>}

        <div style={{ textAlign: "right" }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginRight: 8 }}>Отмена</button>
          <button type="submit" className="btn btn-primary" disabled={mut.isPending || !lines.length}
                  style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 18px", fontWeight: 600, cursor: "pointer" }}>
            {mut.isPending ? "Создание…" : "Создать (черновик)"}
          </button>
        </div>
      </form>
    </div>
  );
}


function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>{children}</div>;
}
